import { Inject, Injectable } from '@angular/core';

import { Image } from '@svgdotjs/svg.js';
import { TrivialError } from 'core/error/error';
import { Point } from 'core/geometry';
import { ImageStoreBaseInformation, ImageStorePostInformation,
         ImageStoreRequestError, ImageStoreService
       } from 'core/imagestore/imagestore.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { ThrottledQueue } from 'core/utils/throttled-queue';
import { GalleryImage, GalleryService } from './gallery.service';
import { checkContentType, fileHash, ImageProvider, toImageStoreError
       } from './imageprovider';

@Injectable()
export class ImageProvisioningService
{
  private static readonly MAX_PARALLEL_POSTS: number = 3;

  private _queue: ThrottledQueue<ImageStorePostInformation> =
      new ThrottledQueue(ImageProvisioningService.MAX_PARALLEL_POSTS);

  constructor(
    @Inject(ImageStoreService) private imageStoreService: ImageStoreService,
    @Inject(GalleryService) private galleryService: GalleryService,
    @Inject(ProductService) private productService: ProductService,
    @Inject(EventTrackingService) private eventTrackingService:
        EventTrackingService,
  )
  { }

  public async addUserFilesToGallery(input: HTMLInputElement): Promise<void>
  {
    let uploadedImagesCount = input.files.length;
    const trackPhotoUpload = (type: string, length: number) => {
      this.eventTrackingService.track({
        category: this.productService.getCurrentProduct().id,
        action: 'gallery_photo_upload',
        label: type,
        value: length,
      });
    };

    const pending = this
      .provideLocalFiles(input.files)
      .map(p => this.galleryService.addPendingImage(p));

    trackPhotoUpload('start', input.files.length);

    await this.galleryService.beginUploadImages();

    const results = await Promise.all(pending.map(p => p.catch(e => e)));
    for (let result of results) {
      if (result instanceof TrivialError) {
        this.galleryService.imageUploadFailed(result);
        uploadedImagesCount--;
      }
    }
    await this.galleryService.flushFailedUploads(input);

    await this.galleryService.finishUploadImages();
    trackPhotoUpload('finished', uploadedImagesCount);
    input.value = '';
  }

  public async addUserFileToPlaceholder(
    input: HTMLInputElement, target: Image): Promise<void>
  {
    if (input.files.length > 1) {
      this.addUserFilesToGallery(input);
    }
    else {
      const pending = this.galleryService.addPendingImage(
        this.provideLocalFile(input.files.item(0)),
        target);

      await this.galleryService.beginUploadImages();

      try {
        await pending;
      } catch (error) {
        this.galleryService.imageUploadFailed(error, target);
        await this.galleryService.flushFailedUploads(input);
      }

      await this.galleryService.finishUploadImages();
    }
  }

  /**
   * @throws ImageUploadError
   * @throws UnsupportedMediaTypeError
   * @throws ImageProvisionError
   */
  private provideLocalFile(file: File): ImageProvider
  {
    this.galleryService.imagesToUpload += 1;
    return async (id: string) =>
      await this.resolveImageStoreUpload(
        id,
        await fileHash(file),
        file.name,
        await this.tryPostFileToImageStore(file)
      );
  }

  /**
   * @throws ImageUploadError
   * @throws UnsupportedMediaTypeError
   * @throws ImageProvisionError
   */
  private provideLocalFiles(files: FileList): Array<ImageProvider>
  {
    const providers: Array<ImageProvider> = [];

    for (let i = 0; i < files.length; ++i) {
      providers.push(this.provideLocalFile(files.item(i)));
    }

    return providers;
  }

  /**
   * @throws ImageUploadError
   * @throws UnsupportedMediaTypeError
   * @throws ImageProvisionError
   */
  private async resolveImageStoreUpload(
    id: string,
    hash: string,
    cause: string,
    postInformation: ImageStoreBaseInformation): Promise<GalleryImage>
  {
    await checkContentType(postInformation.contentType, cause);

    let contentCenter = undefined;
    if (postInformation.contentCenter) {
      const splitted = postInformation.contentCenter.split(',');
      contentCenter = new Point(
          parseFloat(splitted[0].trim()), parseFloat(splitted[1].trim()));
    }

    return {
      id: id,
      url: postInformation.location,
      traudlUrls: {},
      width: postInformation.width,
      height: postInformation.height,
      hash: hash,
      expirationDate: postInformation.expirationDate,
      contentCenter: contentCenter,
      isLoaded: false
    };
  }

  /**
   * @throws ImageUploadError
   * @throws UnsupportedMediaTypeError
   * @throws ImageProvisionError
   * @throws ImageSizeLimitExceededError
   */
  private async tryPostFileToImageStore(file: File):
    Promise<ImageStorePostInformation>
  {
    try {
      const resp = await this._queue.run(
          () => this.imageStoreService.postImage(file));

      this.galleryService.imagesUploaded += 1;
      return resp;
    } catch (error) {
      this.galleryService.imagesToUpload -= 1;
      throw toImageStoreError(error as ImageStoreRequestError, file.name);
    }
  }
}
